import {
  buildPath,
  buildTemplatePath,
  CrudMode,
  ListContext,
  ListContextValue,
  newToast,
  Page,
  PageRoute,
  useParams,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import { AspNetUserPermission, LkRoleId, Permission, TenantInfo, User, UserEditResource, UserViewResource } from "generated/openApi";
import "moment-timezone";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { UserPageBody } from "./UserPageBody/UserPageBody";
import { UserPageHeader } from "./UserPageHeader";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "redux/store";
import { setAppToast } from "redux/reducers/appSlice";

const routeName = "user";

class Params {
  membership: string = "0";
  crudMode: CrudMode = "view";
}

export const userPageRoute: PageRoute<Params> = {
  handle: "User",
  templatePath: buildTemplatePath(routeName, Params),
  getPath: (params) => buildPath(routeName, params),
};

export const UserPage = () => {
  const ROOT_PERMISSION: Permission = Permission.User;
  const navigate = useNavigate();
  const { params } = useParams(routeName, Params);
  const [user, setUser] = useState<User>({} as User);
  const [userPermissions, setUserPermissions] = useState<number[]>([]);
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [user, userPermissions] });
  const [editResource, setEditResource] = useState<UserEditResource>({});
  const [viewResource, setViewResource] = useState<UserViewResource>({});
  const [tenantInfo, setTenantInfo] = useState<TenantInfo>();
  const { validationErrors, setValidationErrors } = useValidationErrors();
  
  const dispatch = useDispatch();
  const listContext = useContext<ListContextValue>(ListContext);

  // Gets the current logged in user's role
  const appUserRoleId: number | undefined = useSelector((state: RootState) => state.app.roleId);

  // Gets the current logged in user's membership
  const appUserMembership: string | undefined = useSelector((state: RootState) => state.app.userMembership);

  useEffect(() => {
    fetch(params.membership);

    ApiService.users.getViewResource().then((response) => {
      setViewResource(response);
    });
    ApiService.users.getUserPermissions(params.membership).then((response) => {
      setUserPermissions(response.map(r => r.permissionId));
    });
    ApiService.users.getEditResource().then((response) => {
      setEditResource(response);
    });
    ApiService.textingConversations.getTenantTextingInfo().then((response) =>{
      setTenantInfo(response);
    });
  }, []);

  // useEffect(() => {
  //   if (params.crudMode === "edit")
  //     ApiService.users.getEditResource().then((response) => {
  //       setEditResource(response);
  //     });
  // }, [params.crudMode]);

  const reroute = (membership: string, crudMode: CrudMode) =>
    navigate(`../${userPageRoute.getPath({ membership, crudMode })}`);

  const fetch = (membership: string) => {
    if (membership == "0") {
      ApiService.users.get(Number.parseInt(membership)).then((response) => {
        setUser(response);
        resetUnsavedChanges([response]);
      });
    }
    else {
      ApiService.users.getByUserMembership(membership).then((response) => {
        setUser(response);
        resetUnsavedChanges([response]);
      });
    }
  };

  const save = () => {
      setValidationErrors({});
  
      if (params.membership && params.membership != "0") {
        if (params.membership === appUserMembership) {
          ApiService.users
            .saveUpdateSelf(user)
            .then(() => {
              fetch(params.membership);
              listContext.loadList?.();
              dispatch(setAppToast(newToast("success")));
              reroute(params.membership, "view");
            })
            .catch((ex) => {
              dispatch(setAppToast(newToast("error")));
              setValidationErrors(ex.response);
            });
          if (appUserRoleId == LkRoleId.Cobro || appUserRoleId == LkRoleId.Administrator) {
            ApiService.users.savePermissions(userPermissions, user.id).then(() => {
            })
              .catch((ex) => {
                console.log("ex");
              });
          }
        }
        else {
          ApiService.users
            .saveUpdate(user)
            .then(() => {
              fetch(params.membership);
              listContext.loadList?.();
              dispatch(setAppToast(newToast("success")));
              reroute(params.membership, "view");
            })
            .catch((ex) => {
              dispatch(setAppToast(newToast("error")));
              setValidationErrors(ex.response);
            });
          ApiService.users.savePermissions(userPermissions, user.id).then(() => {
  
          })
            .catch((ex) => {
              console.log("ex");
            });
        }
      }
      else {
        ApiService.users
          .saveCreate(user)
          .then((res) => {
            const id = res;
            listContext.loadList?.();
            // send user invitation email
            ApiService.users.sendInvitationEmail(res).then((response) => {
              fetch(response);
              reroute(response, "view");
              dispatch(setAppToast(newToast("success")));
            });
  
            dispatch(setAppToast(newToast("success")));
          })
          .catch((ex) => {
            dispatch(setAppToast(newToast("error")));
            setValidationErrors(ex.response);
          });
      };
    }

  return (
    <Page
      isForm
      header={
        <UserPageHeader
          rootPermission={ROOT_PERMISSION}
          userMembership={params.membership}
          handle={userPageRoute.handle}
          crudMode={params.crudMode}
          reroute={reroute}          
          fetch={fetch}
          user={user}
          appUserRoleId={appUserRoleId}
          userPermissions={userPermissions}
          setValidationErrors={setValidationErrors}
          unsavedChanges={unsavedChanges}
        />
      }
    >
      <UserPageBody
        user={user}
        appUserRoleId={appUserRoleId} 
        userPermissions={userPermissions}
        userMembership={params.membership}
        reroute={reroute}
        tenantInfo={tenantInfo}
        setUser={setUser}
        save={save}
        unsavedChanges={unsavedChanges}
        setUserPermissions={setUserPermissions}
        crudMode={params.crudMode}
        viewResource={viewResource}
        editResource={editResource}
        validationErrors={validationErrors}
      />
    </Page>
  );
};
